import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import { defaultGridContainerProps, defaultGridItemProps, getPageTitle } from 'util/Layout';
import useRequest from 'hooks/useRequest';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { ConfigContext } from 'components/ConfigGuard';
import MakePaymentPageView from 'components/payments/MakePaymentPageView';
import OrderHistoryPageView from 'components/payments/OrderHistoryPageView';
import CompanyService from 'services/api/CompanyService';
import routes from 'store/configs/Routes';
import { useHistory, useLocation } from 'react-router';
import CompanySelect from 'components/company/CompanySelect';
import PartnerPayButton from 'components/payments/PartnerPayButton';
import ButtonGroupWithBadge, { ButtonGroupWithBadgeItem } from 'components/shared/ButtonGroupWithBadge';
import SiteModule from 'store/enums/SiteModule';
import {
  ALL_COMPANIES_DROPDOWN_KEY,
  SELECT_COMPANY_DROPDOWN_KEY,
  getCompanySelectOptions,
  CompanySelectOption,
  ALL_PROGRAMS_OPTION,
} from 'util/Company';
import Tile from 'components/shared/Tile';
import { getPreselectedOrderNumbers, getPrice } from 'util/Payment';
import { CompanyPaymentDetails } from 'store/types/Company';
import DownloadStatementModal from 'components/payments/DownloadStatementModal';
import ApplyCreditsToggle from 'components/payments/ApplyCreditsToggle';
import {
  CompanyPaymentsPageInvoiceStatusViewModel,
  CompanyPaymentsPageSalesOrderStatusViewModel,
  CompanyPaymentsPageViewModel,
  InvoiceStatusKey,
  SalesOrderStatusKey,
} from 'store/types/CompanyPaymentsPageViewModel';
import { OrderPayment } from 'store/types/Payment';
import OrderType from 'store/enums/OrderType';
import PaymentStatus from 'store/enums/PaymentStatus';
import { OrderRecordType } from 'components/payments/OrderHistoryPageView/OrderHistoryTable/OrderHistoryTableReducer';
import { UserPrograms } from 'store/types/User';
import { ParametersContext } from 'components/ParametersGuard';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';
import styles from './CompanyPaymentPage.module.scss';

const tabConfig = {
  CompanyPayments: { key: 'CompanyPayments', label: 'Company Invoices', route: routes.companyPayments } as TabItem,
  MakeCompanyPayment: {
    key: 'MakeCompanyPayment',
    label: 'Pay Company Invoices',
    route: routes.makeCompanyPayment,
  } as TabItem,
  MakeCompanySalesPayment: {
    key: 'MakeCompanySalesPayment',
    label: 'Pay Company Sales Orders',
    route: routes.makeCompanySalesPayment,
  } as TabItem,
};

const getInvoicePaymentStatus = ({ key, isOverdue }: CompanyPaymentsPageInvoiceStatusViewModel): PaymentStatus => {
  switch (key) {
    case InvoiceStatusKey.PaidInFull:
      return PaymentStatus.Paid;
    case InvoiceStatusKey.Open:
      return isOverdue ? PaymentStatus.Overdue : PaymentStatus.Unpaid;
    default:
      return PaymentStatus.Processing;
  }
};

// Pending fulfillment and partially fulfilled orders are not billable yet, so they share one status
const getSalesOrderPaymentStatus = ({ key }: CompanyPaymentsPageSalesOrderStatusViewModel): PaymentStatus => {
  switch (key) {
    case SalesOrderStatusKey.PendingBilling:
      return PaymentStatus.PendingBilling;
    case SalesOrderStatusKey.FullyBilled:
      return PaymentStatus.FullyBilled;
    default:
      return PaymentStatus.PendingFulfillment;
  }
};

// A payment is applied to a single subsidiary, so invoices can only be selected together within one subsidiary
const getInvoiceSelectionGroup = ({ subsidiaryId = '' }: OrderPayment): string => subsidiaryId;

const CompanyPaymentsPage: React.FunctionComponent = () => {
  const { replace } = useHistory();
  const { pathname, search } = useLocation();
  const preselectedOrderNumbers = useMemo(() => getPreselectedOrderNumbers(search), [search]);
  const { functions = [], userLoading, userPermissions } = useContext(UserContext);
  const {
    sidebar,
    isNccerTheme,
    isSmacnaTheme,
    modulesSettings: {
      companyPayments: { enablePaySalesOrders, hiddenTabs, partnerPayLink },
    },
  } = useContext(ConfigContext);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedProgramId, setSelectedProgramId] = useState<string>(ALL_PROGRAMS_OPTION);
  const [downloadStatementModalOpen, setDownloadStatementModalOpen] = useState<boolean>(false);
  const [showSelectCompanyMenuItem, setShowSelectCompanyMenuItem] = useState<boolean | null>(null);
  const [subsidiaryId, setSubsidiaryId] = useState<string>('');
  const permissions: string[] = useMemo(
    () => userPermissions.modules.find(({ name }) => name === SiteModule.Company)?.permissions || [],
    [userPermissions]
  );
  const {
    companyManagement: { companyDropdownName },
  } = useContext(ParametersContext);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const companyDetailsRequest = useMemo(
    () =>
      selectedCompanyId &&
      selectedCompanyId !== ALL_COMPANIES_DROPDOWN_KEY &&
      selectedCompanyId !== SELECT_COMPANY_DROPDOWN_KEY
        ? () => CompanyService.getCompanyPaymentDetails(selectedCompanyId)
        : undefined,
    [selectedCompanyId]
  );

  const companyDetailsRequestData = useRequest<CompanyPaymentDetails>(companyDetailsRequest);
  const { data: companyDetailData, loading: companyDetailLoading } = companyDetailsRequestData;
  const userPrograms: UserPrograms[] = useMemo(() => companyDetailData?.programs || [], [companyDetailData?.programs]);
  const balanceDue: number | undefined = useMemo(() => companyDetailData?.balance, [companyDetailData]);
  const ordersReqDataAllCompanies = useRequest<CompanyPaymentsPageViewModel>(
    CompanyService.getCompanyPaymentsPageViewModel
  );
  const {
    data: companyPaymentPageViewModel,
    firstLoading: ordersFirstLoadingAllCompanies,
    loading: loadingAllCompaniesData,
  } = ordersReqDataAllCompanies;
  const companyInvoices: OrderPayment[] = useMemo(
    () =>
      (companyPaymentPageViewModel?.companyDetails ?? []).flatMap(({ name: companyName, subsidiaries, invoices }) =>
        invoices.map((invoice) => ({
          id: invoice.id,
          recordType: OrderRecordType.Invoice,
          type: OrderType.CustInvc,
          status: getInvoicePaymentStatus(invoice.status),
          orderNumber: invoice.invoiceNumber,
          invoiceName: '',
          orderDate: String(invoice.orderDate),
          dueDate: invoice.dueDate ? String(invoice.dueDate) : undefined,
          totalAmount: invoice.totalAmount,
          amountPaid: invoice.previousPayments,
          amountRemaining: invoice.balanceDue,
          billTo: companyName,
          ownerId: invoice.companyId,
          ownerName: companyName,
          programId: invoice.programId,
          subsidiaryId: invoice.subsidiaryId,
          subsidiaryName: subsidiaries.find(({ id }) => id === invoice.subsidiaryId)?.name,
        }))
      ),
    [companyPaymentPageViewModel]
  );
  const programInvoices: OrderPayment[] = useMemo(
    () =>
      selectedProgramId === ALL_PROGRAMS_OPTION
        ? companyInvoices
        : companyInvoices.filter(({ programId }) => programId === selectedProgramId),
    [companyInvoices, selectedProgramId]
  );
  const companySalesOrders: OrderPayment[] = useMemo(
    () =>
      (companyPaymentPageViewModel?.companyDetails ?? []).flatMap(({ name: companyName, subsidiaries, salesOrders }) =>
        salesOrders.map((salesOrder) => {
          const status = getSalesOrderPaymentStatus(salesOrder.status);

          return {
            id: salesOrder.id,
            recordType: OrderRecordType.SalesOrder,
            type: OrderType.SalesOrd,
            status,
            orderNumber: salesOrder.salesOrderNumber,
            invoiceName: salesOrder.salesOrderNumber,
            orderDate: String(salesOrder.orderDate),
            totalAmount: salesOrder.totalAmount,
            amountPaid: salesOrder.totalAmount,
            amountRemaining: status === PaymentStatus.PendingFulfillment ? 0 : salesOrder.totalAmount,
            billTo: companyName,
            ownerId: salesOrder.companyId,
            ownerName: companyName,
            programId: salesOrder.programId,
            subsidiaryId: salesOrder.subsidiaryId,
            subsidiaryName: subsidiaries.find(({ id }) => id === salesOrder.subsidiaryId)?.name,
          };
        })
      ),
    [companyPaymentPageViewModel]
  );
  // Sales orders are not filtered by program, matching the previous orders endpoints
  const companyOrdersRequestData = useMemo(
    () => ({ ...ordersReqDataAllCompanies, data: [...programInvoices, ...companySalesOrders] }),
    [ordersReqDataAllCompanies, programInvoices, companySalesOrders]
  );
  const isAllCompanies = !selectedCompanyId || selectedCompanyId === ALL_COMPANIES_DROPDOWN_KEY;
  // All Companies shows every invoice across companies and subsidiaries, so the subsidiary filter only applies to a single company
  const payableSubsidiaryId = isAllCompanies ? '' : subsidiaryId;
  const payableInvoicesRequestData = useMemo(
    () => ({
      ...ordersReqDataAllCompanies,
      data: programInvoices
        .filter(
          ({ ownerId, subsidiaryId: invoiceSubsidiaryId }) =>
            (isAllCompanies || ownerId === selectedCompanyId) &&
            (!payableSubsidiaryId || invoiceSubsidiaryId === payableSubsidiaryId)
        )
        .sort(
          (a, b) =>
            (a.ownerName ?? '').localeCompare(b.ownerName ?? '') ||
            (a.subsidiaryName ?? '').localeCompare(b.subsidiaryName ?? '') ||
            a.orderDate.localeCompare(b.orderDate)
        ),
    }),
    [ordersReqDataAllCompanies, programInvoices, isAllCompanies, selectedCompanyId, payableSubsidiaryId]
  );
  const payableSalesOrdersRequestData = useMemo(
    () => ({
      ...ordersReqDataAllCompanies,
      data: companySalesOrders.filter(({ ownerId }) => isAllCompanies || ownerId === selectedCompanyId),
    }),
    [ordersReqDataAllCompanies, companySalesOrders, isAllCompanies, selectedCompanyId]
  );
  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);

  const options: CompanySelectOption[] = useMemo(
    () =>
      showSelectCompanyMenuItem !== null
        ? getCompanySelectOptions(
            functions,
            permissions,
            activeTab !== tabConfig.CompanyPayments.key,
            showSelectCompanyMenuItem
          )
        : [],
    [activeTab, functions, permissions, showSelectCompanyMenuItem]
  );

  const subsidiaryButtonItems: ButtonGroupWithBadgeItem[] = useMemo(() => {
    const companies = (companyPaymentPageViewModel?.companyDetails ?? []).filter(
      ({ id }) => !selectedCompanyId || selectedCompanyId === ALL_COMPANIES_DROPDOWN_KEY || id === selectedCompanyId
    );
    const openInvoiceCounts = companies
      .flatMap(({ invoices }) => invoices)
      .filter(({ status }) => status.key === InvoiceStatusKey.Open)
      .reduce<Record<string, number>>(
        (counts, { subsidiaryId }) => ({ ...counts, [subsidiaryId]: (counts[subsidiaryId] ?? 0) + 1 }),
        {}
      );
    const subsidiaries = new Map(companies.flatMap(({ subsidiaries }) => subsidiaries).map((s) => [s.id, s]));

    return Array.from(subsidiaries.values(), ({ id, name }) => ({
      id,
      label: name,
      count: openInvoiceCounts[id] ?? 0,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [companyPaymentPageViewModel, selectedCompanyId]);

  useEffect(() => {
    setSelectedProgramId(ALL_PROGRAMS_OPTION);
    if (activeTab === tabConfig.MakeCompanyPayment.key || activeTab === tabConfig.MakeCompanySalesPayment.key) {
      setShowSelectCompanyMenuItem(true);
      setSubsidiaryId('');
    } else {
      setShowSelectCompanyMenuItem(false);
    }
  }, [activeTab]);

  // Applies a deep link (?orderNumbers=) once per search value: selects the invoice's company and subsidiary together,
  // so no other effect can reset one after the other has been set
  const appliedPreselectionRef = useRef<string>();

  useEffect(() => {
    if (
      !preselectedOrderNumbers.length ||
      appliedPreselectionRef.current === search ||
      (activeTab !== tabConfig.MakeCompanyPayment.key && activeTab !== tabConfig.MakeCompanySalesPayment.key)
    ) {
      return;
    }

    const matchedOrder = companyPaymentPageViewModel?.companyDetails
      .flatMap(({ invoices, salesOrders }) =>
        activeTab === tabConfig.MakeCompanySalesPayment.key
          ? salesOrders.map(({ salesOrderNumber, companyId, subsidiaryId }) => ({
              orderNumber: salesOrderNumber,
              companyId,
              subsidiaryId,
            }))
          : invoices.map(({ invoiceNumber, companyId, subsidiaryId }) => ({
              orderNumber: invoiceNumber,
              companyId,
              subsidiaryId,
            }))
      )
      .find(({ orderNumber }) => preselectedOrderNumbers.includes(orderNumber));

    if (!matchedOrder) {
      return;
    }

    appliedPreselectionRef.current = search;
    setSelectedCompanyId(matchedOrder.companyId);
    setSubsidiaryId(matchedOrder.subsidiaryId);
  }, [activeTab, search, preselectedOrderNumbers, companyPaymentPageViewModel]);

  const handleCompanyChange = useCallback(
    (newCompanyId: string) => {
      setSelectedCompanyId(newCompanyId);
      setSelectedProgramId(ALL_PROGRAMS_OPTION);
      setSubsidiaryId('');

      if (preselectedOrderNumbers.length && newCompanyId !== ALL_COMPANIES_DROPDOWN_KEY) {
        replace({ pathname, search: '' });
      }
    },
    [replace, pathname, preselectedOrderNumbers]
  );

  const handleProgramSelectChange = useCallback((value: string) => {
    setSelectedProgramId(value);
  }, []);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.CompanyPayments.key) => {
      replace(getTabRouteByKey(value, tabConfig));
    },
    [replace]
  );

  useEffect(() => {
    replace({ pathname: getDefaultTabRoute(pathname, visibleTabs), search });
  }, [pathname, replace, search, visibleTabs]);

  const handleDownloadStatementModalOpen = useCallback(() => {
    setDownloadStatementModalOpen(true);
  }, []);

  const handleDownloadStatementModalClose = useCallback(() => {
    setDownloadStatementModalOpen(false);
  }, []);

  useEffect(() => {
    if (subsidiaryButtonItems.length === 1) {
      setSubsidiaryId(subsidiaryButtonItems[0].id);
    }
  }, [subsidiaryButtonItems]);

  const handleSubsidiaryChange = (id: string) => {
    setSubsidiaryId(id === subsidiaryId ? '' : id);
  };

  const shouldShowGenerateStatement = (id: string | undefined) =>
    id && ![ALL_COMPANIES_DROPDOWN_KEY, SELECT_COMPANY_DROPDOWN_KEY].some((key) => key === id);

  const getTabPanelContent = useCallback(
    (children: React.ReactNode, displayApplyCreditToggle?: boolean): React.ReactNode => (
      <Grid {...defaultGridContainerProps} justifyContent={'flex-end'}>
        {isNccerTheme && selectedCompanyId !== ALL_COMPANIES_DROPDOWN_KEY && (
          <Grid {...defaultGridItemProps} xs={true}>
            <div className={styles.tilesWrapper}>
              <Tile
                value={getPrice(balanceDue)}
                label={'Account Balance'}
                classes={{ value: styles.value, label: styles.label }}
                loading={companyDetailLoading}
                theme={'dark-red'}
                className={styles.tile}
              />
              <span className={styles.helperText}>
                {'A negative account balance indicates that there is a credit on the account.'}
              </span>
            </div>
          </Grid>
        )}
        {selectedCompanyId === ALL_COMPANIES_DROPDOWN_KEY && partnerPayLink ? (
          <Grid {...defaultGridItemProps} xs={'auto'} alignItems={'left'} marginRight={'auto'}>
            <PartnerPayButton label={partnerPayLink.label} link={partnerPayLink.link} />
          </Grid>
        ) : (
          <>
            {partnerPayLink && (
              <Grid {...defaultGridItemProps} xs={'auto'} marginRight={'auto'}>
                <PartnerPayButton label={partnerPayLink.label} link={partnerPayLink.link} />
              </Grid>
            )}
            {displayApplyCreditToggle && (
              <Grid {...defaultGridItemProps} xs={'auto'}>
                <ApplyCreditsToggle id={selectedCompanyId} {...companyDetailsRequestData} />
              </Grid>
            )}
            {shouldShowGenerateStatement(selectedCompanyId) && (
              <Grid {...defaultGridItemProps} xs={'auto'}>
                <Button variant={'contained'} color={'secondary'} onClick={handleDownloadStatementModalOpen}>
                  {'Generate Statement'}
                </Button>
              </Grid>
            )}
          </>
        )}
        <Grid {...defaultGridItemProps}>{children}</Grid>
      </Grid>
    ),
    [
      isNccerTheme,
      selectedCompanyId,
      balanceDue,
      companyDetailLoading,
      partnerPayLink,
      companyDetailsRequestData,
      handleDownloadStatementModalOpen,
    ]
  );

  return (
    <>
      <DownloadStatementModal
        {...companyDetailsRequestData}
        open={downloadStatementModalOpen}
        id={selectedCompanyId}
        onClose={handleDownloadStatementModalClose}
      />
      <h1 className={commonStyles.pageTitle}>
        {getPageTitle(sidebar, SiteModule.Payments, SiteModule.CompanyPayments, !isSmacnaTheme) || 'Manage Company'}
      </h1>
      <Spinner loading={userLoading || loadingAllCompaniesData} transparent={false}>
        <TabContext value={activeTab}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor={'primary'}
            textColor={'primary'}
            variant={'scrollable'}
          >
            {visibleTabs.map((tab) => {
              if (tab.key === tabConfig.MakeCompanySalesPayment.key && !enablePaySalesOrders) {
                return;
              }

              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  className={commonStyles.tab}
                  classes={{ selected: commonStyles.activeTab }}
                />
              );
            })}
          </Tabs>
          <div className={styles.companySelect}>
            {showSelectCompanyMenuItem !== null && (
              <CompanySelect
                value={selectedCompanyId}
                options={options}
                onChange={handleCompanyChange}
                size={'small'}
                label={companyDropdownName || 'Select Company'}
                showAllCompaniesMenuItem={
                  (activeTab === tabConfig.MakeCompanyPayment.key ||
                    activeTab === tabConfig.MakeCompanySalesPayment.key) &&
                  showSelectCompanyMenuItem
                }
              />
            )}
          </div>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.CompanyPayments.key}>
            {getTabPanelContent(
              !!selectedCompanyId && (
                <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                  <OrderHistoryPageView
                    {...companyOrdersRequestData}
                    companyId={selectedCompanyId}
                    makePaymentRoute={routes.makeCompanyPayment}
                    openOrdersEnabled={true}
                    onProgramChange={handleProgramSelectChange}
                    programId={selectedProgramId}
                    programsList={userPrograms}
                    loadTransactionDetails={true}
                  />
                </Spinner>
              ),
              true
            )}
          </TabPanel>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakeCompanyPayment.key}>
            <Grid {...defaultGridContainerProps}>
              <Grid {...defaultGridItemProps}>
                {!isAllCompanies && subsidiaryButtonItems.length > 1 && (
                  <Box sx={{ marginBottom: 4 }}>
                    <ButtonGroupWithBadge
                      items={subsidiaryButtonItems}
                      selectedId={subsidiaryId}
                      showZeroCounts={true}
                      size={'large'}
                      ungrouped={true}
                      onChange={handleSubsidiaryChange}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>

            {getTabPanelContent(
              <>
                <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                  <MakePaymentPageView
                    {...payableInvoicesRequestData}
                    companyId={selectedCompanyId}
                    subsidiaryId={payableSubsidiaryId}
                    onProgramChange={handleProgramSelectChange}
                    programId={selectedProgramId}
                    hasMultiSubsidiaries={!isAllCompanies && subsidiaryButtonItems.length > 1}
                    subsidiaryColumnEnabled={subsidiaryButtonItems.length > 1}
                    getSelectionGroup={getInvoiceSelectionGroup}
                    loadTransactionDetails={true}
                  />
                </Spinner>
              </>
            )}
          </TabPanel>
          <TabPanel className={commonStyles.tabPanel} value={tabConfig.MakeCompanySalesPayment.key}>
            {getTabPanelContent(
              <Spinner loading={ordersFirstLoadingAllCompanies} transparent={false}>
                <MakePaymentPageView
                  {...payableSalesOrdersRequestData}
                  companyId={selectedCompanyId}
                  subsidiaryId={subsidiaryId}
                  onProgramChange={handleProgramSelectChange}
                  programId={selectedProgramId}
                  salesOrders={true}
                  loadTransactionDetails={true}
                />
              </Spinner>
            )}
          </TabPanel>
        </TabContext>
      </Spinner>
    </>
  );
};

export default CompanyPaymentsPage;
